import { ElMessage, ElMessageBox } from "element-plus";
import { reactive, ref } from "vue";
import {
  queryTableIn,
  batchDeleteByIds,
  changeInvoicingStatus,
  handleProcessApi,
  queryBoundTotal,
} from "@/api/bound";
import { postDownload } from "@/utils";

export default function useInStorageTable() {
  // 表单搜索ref
  const searchRef = ref(null);

  // 表格ref
  const tableRef = ref(null);

  // 新增编辑的ref
  const editRef = ref(null);

  // 搜索表单
  const searchData = reactive({
    number: null,
    boundType: "ACCEPT_MATERIAL",
    warehouseId: null,
    materialNameText: null,
    deliveryUser: null,
    remark: null,
    processStatus: null,
    invoicingStatus: null,
    startTime: null,
    endTime: null,
    person: null,
    type: null,
  });

  // 表格列配置
  const columns = ref([
    {
      label: "申请单号",
      prop: "number",
    },
    {
      label: "申请部门",
      prop: "requestDepartmentText",
    },
    {
      label: "申请物料",
      prop: "materialNameText",
    },
    {
      label: "物料用途",
      prop: "typeText",
    },
    {
      label: "申请人",
      prop: "boundUserName",
    },
    {
      label: "申请时间",
      prop: "boundTime",
    },
    {
      label: "审核状态",
      prop: "processStatus",
    },
  ]);

  // 表格数据
  const tableData = ref([]);

  // 表格遮罩
  const tableLoading = ref(false);

  // 分页数据
  const pages = reactive({
    page: 1,
    size: 10,
    total: 0,
  });
  const handleSizeChange = (val) => {
    pages.size = val;
    pages.page = 1;
    getList();
  };
  const handleCurrentChange = (val) => {
    pages.page = val;
    getList();
  };
  // 日期范围选择器
  const datePicker = ref("");

  // 多选表格的ids
  const selectionIds = ref([]);

  // 点击重置
  const handleReset = () => {
    searchRef.value?.resetFields();
    datePickerChange(null);
    datePicker.value = "";
    pages.page = 1;
    getList();
  };

  // 获取表格数据
  const getList = () => {
    tableLoading.value = true;

    queryTableIn({
      ...searchData,
      page: pages.page,
      limit: pages.size,
    })
      .then((res) => {
        tableData.value = res.data.records;
        pages.total = res.data.total;
        getStatistics();
      })
      .finally(() => {
        tableLoading.value = false;
      });
  };
  // 统计数据
  const statistics = ref({});
  const getStatistics = () => {
    queryBoundTotal({ ...searchData })
      .then((res) => {
        statistics.value = res.data;
        statistics.value.count = pages.total;
      })
      .finally(() => {
        tableLoading.value = false;
      });
  };

  // 点击新增或编辑
  const handleAdjust = (row, type) => {
    editRef.value?.handleDialogOpen({ row, type });
  };

  // 时间范围选择器变化
  const datePickerChange = (val) => {
    searchData.startTime = val?.[0] ?? null;
    searchData.endTime = val?.[1] ?? null;
  };

  const handleSelectionChange = (val) => {
    selectionIds.value = [];
    val.forEach((item) => {
      selectionIds.value.push(item.id);
    });
    console.log(selectionIds.value);
  };

  const handleBatchDelete = (id, type) => {
    if (type) {
      if (selectionIds.value.length == 0)
        return ElMessage.warning("未选择任何数据");
      handleDelete(selectionIds.value.join(","));
    } else {
      handleDelete([id].join(","));
    }
  };

  // 批量删除
  const handleDelete = (ids) => {
    ElMessageBox.confirm("确定删除所选数据吗?", "删除", {
      confirmButtonText: "确定",
      cancelButtonText: "取消",
      type: "error",
    })
      .then(() => {
        batchDeleteByIds(ids).then((res) => {
          if (res.code == "2000") {
            ElMessage.success("操作成功");
            getList();
          }
        });
      })
      .catch(() => {
        ElMessage.info("已取消");
      })
      .finally(() => {
        selectionIds.value = [];
        tableRef.value?.clearSelection();
      });
  };

  // 导出
  const handleExport = () => {
    ElMessageBox.confirm("确定导出申请数据吗?", "导出", {
      confirmButtonText: "确定",
      cancelButtonText: "取消",
      type: "success",
    })
      .then(() => {
        postDownload("/bound/export", {}, "申请记录.xlsx");
      })
      .catch(() => {
        ElMessage.info("已取消");
      });
  };

  // 开票
  const markers = () => {
    if (selectionIds.value.length == 0)
      return ElMessage.warning("未选择任何数据");
    ElMessageBox.confirm("确定要标记已开票?", {
      confirmButtonText: "确定",
      cancelButtonText: "取消",
      type: "success",
    })
      .then(() => {
        let submitData = [];
        selectionIds.value.forEach((item) => {
          let obj = {
            id: item,
            invoicingStatus: "1",
          };
          submitData.push(obj);
        });
        changeInvoicingStatus(submitData).then((res) => {
          if (res.code == "2000") {
            ElMessage.success("操作成功");
            getList();
          }
        });
      })
      .catch(() => {
        ElMessage.info("已取消");
      })
      .finally(() => {
        selectionIds.value = [];
        tableRef.value?.clearSelection();
      });
  };
  // 取消开票
  const cancerMarkers = () => {
    if (selectionIds.value.length == 0)
      return ElMessage.warning("未选择任何数据");
    ElMessageBox.confirm("确定要取消已开票?", {
      confirmButtonText: "确定",
      cancelButtonText: "取消",
      type: "success",
    })
      .then(() => {
        let submitData = [];
        selectionIds.value.forEach((item) => {
          let obj = {
            id: item,
            invoicingStatus: "0",
          };
          submitData.push(obj);
        });
        changeInvoicingStatus(submitData).then((res) => {
          if (res.code == "2000") {
            ElMessage.success("操作成功");
            getList();
          }
        });
      })
      .catch(() => {
        ElMessage.info("已取消");
      })
      .finally(() => {
        selectionIds.value = [];
        tableRef.value?.clearSelection();
      });
  };
  // 提交审核
  const handleProcess = (id) => {
    ElMessageBox.confirm("确定要提交审核?", {
      confirmButtonText: "确定",
      cancelButtonText: "取消",
      type: "success",
    })
      .then(() => {
        handleProcessApi(id).then((res) => {
          if (res.code == "2000") {
            ElMessage.success("操作成功");
            getList();
          }
        });
      })
      .catch(() => {
        ElMessage.info("已取消");
      })
      .finally(() => {
        selectionIds.value = [];
        tableRef.value?.clearSelection();
      });
  };

  // 点击导入
  const uploadRef = ref(null);
  const handleUpload = () => {
    uploadRef.value?.handleDialogOpen();
  };
  // 导入参数
  const uploadConfig = reactive({
    templateUrl: "/bound/downloadPicking", // 模板下载的url
    name: "领料导入模板.xlsx", // 模板文件名
  });

  return {
    searchRef,
    searchData,
    columns,
    tableData,
    statistics,
    tableLoading,
    pages,
    handleSizeChange,
    handleCurrentChange,
    editRef,
    datePicker,
    tableRef,
    handleReset,
    getList,
    handleAdjust,
    handleExport,
    datePickerChange,
    handleSelectionChange,
    handleBatchDelete,
    markers,
    cancerMarkers,
    handleProcess,
    handleUpload,
    uploadRef,
    uploadConfig,
  };
}
